import React, { useEffect, useState, useRef } from "react";
import Input from "../../../components/input";
import useRequest from "../../../components/hook/use-req";
import { ClockLoader } from "react-spinners";
import { toast } from "sonner";
import useApi from "../../../components/hook/req";
import ConversationSettings from "./conversation-settings";
import TestingPreviewSettings from "./testing-preview";
import { isConversationAgent } from "../../../utils/org-role";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SettingsFormData {
  firstName: string;
  lastName: string;
  businessName: string;
  website: string;
  businessLocation: string;
  email: string;
  phone: string;
  logo: File | string | null;
}

// ─── ProfilePicture ───────────────────────────────────────────────────────────

interface ProfilePictureProps {
  name: string;
  logo: File | string | null;
  subtitle?: string;
  onDelete: () => void;
  onUpload: (file: File) => void;
  uploadLoading?: boolean;
  // conversation_agent can't change the company logo (or anything else on
  // this tab, but the rest of "company profile" was already read-only for
  // everyone before this — the logo was the one actually-editable field).
  readOnly?: boolean;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  name,
  logo,
  subtitle,
  onDelete,
  onUpload,
  uploadLoading,
  readOnly,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size should be less than 2MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }
      onUpload(file);
    }
  };

  // Get preview URL - if logo is a string (URL), use it; if it's a File, create preview
  const getPreviewUrl = () => {
    if (!logo) return null;
    if (typeof logo === 'string') return logo;
    if (logo instanceof File) return URL.createObjectURL(logo);
    return null;
  };

  const previewUrl = getPreviewUrl();

  return (
    <div className="flex items-start justify-between py-6">
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xl overflow-hidden">
          {previewUrl ? (
            <img src={previewUrl} alt="Logo" className="w-full h-full object-cover" />
          ) : (
            name.charAt(0).toUpperCase()
          )}
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{name}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {!readOnly && (
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={uploadLoading}
          >
            {uploadLoading ? (
              <ClockLoader color="#ffffff" size={16} />
            ) : (
              "Upload New Picture"
            )}
          </button>
          <button
            onClick={onDelete}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ─── SettingsSection ──────────────────────────────────────────────────────────

interface SettingsSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, description, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-8 border-t border-gray-200 dark:border-gray-700">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
    </div>
    <div className="flex flex-col gap-4">{children}</div>
  </div>
);

interface SettingsActionsProps {
  isDirty: boolean;
  onDiscard: () => void;
  onSave: () => void;
  saveLoading?: boolean;
}

const SettingsActions: React.FC<SettingsActionsProps> = ({ isDirty, onDiscard, onSave, saveLoading }) => {
  if (!isDirty) return null;
  return (
    <div className="flex items-center justify-end gap-3 py-4 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={onDiscard}
        className="px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        Discard
      </button>
      <button
        onClick={onSave}
        disabled={saveLoading}
        className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saveLoading ? <ClockLoader color="#ffffff" size={16} /> : "Save Changes"}
      </button>
    </div>
  );
};

// ─── SettingsPage (main) ──────────────────────────────────────────────────────

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"profile" | "conversation" | "testing">("profile");
  const [form, setForm] = useState<SettingsFormData>({
    firstName: "",
    lastName: "",
    businessName: "",
    website: "",
    businessLocation: "",
    email: "",
    phone: "",
    logo: null,
  });
  const [saved, setSaved] = useState<SettingsFormData>({
    firstName: "",
    lastName: "",
    businessName: "",
    website: "",
    businessLocation: "",
    email: "",
    phone: "",
    logo: null,
  });
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  
  const isDirty = JSON.stringify({
    ...form,
    logo: form.logo instanceof File ? "FILE" : form.logo
  }) !== JSON.stringify({
    ...saved,
    logo: saved.logo instanceof File ? "FILE" : saved.logo
  });

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: updateUserData } = useApi(`/profile/all`, "PUT", {
    Authorization: `Bearer ${token}`,
  });

    const { makeRequest: deleteLogo } = useRequest(`/profile/org/logo`, "DELETE", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [response] = await getUserData();

      if (response?.payload) {
        const userData: SettingsFormData = {
          firstName: response.payload.first_name || "",
          lastName: response.payload.last_name || "",
          businessName: response.payload.org_name || "",
          website: response.payload.org_domain_name || "",
          businessLocation: response.payload.country || "",
          email: response.payload.email || "",
          phone: response.payload.phone_number || "",
          logo: response.payload.org_logo || null,
        };
        setForm(userData);
        setSaved(userData);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaveLoading(true);
    
    // Create FormData for the update
    const formData = new FormData();
    formData.append("first_name", form.firstName);
    formData.append("last_name", form.lastName);
    
    // If logo is a File, append it directly
    if (form.logo instanceof File) {
      formData.append("logo", form.logo);
    } else if (typeof form.logo === 'string' && form.logo) {
      // If it's a string URL, send it as is
      formData.append("logo", form.logo);
    }

    const [response] = await updateUserData(formData);

    if (response?.status) {
      toast.success(response.message || "Settings updated successfully");
      // After successful save, fetch fresh data to get the updated logo URL
      const [freshResponse] = await getUserData();
      if (freshResponse?.payload) {
        const userData: SettingsFormData = {
          firstName: freshResponse.payload.first_name || "",
          lastName: freshResponse.payload.last_name || "",
          businessName: freshResponse.payload.org_name || "",
          website: freshResponse.payload.org_domain_name || "",
          businessLocation: freshResponse.payload.country || "",
          email: freshResponse.payload.email || "",
          phone: freshResponse.payload.phone_number || "",
          logo: freshResponse.payload.logo || null,
        };
        setForm(userData);
        setSaved(userData);
      }
    } else {
      toast.error(response?.message || "Failed to update settings");
    }
    setSaveLoading(false);
  };

  const handleDiscard = () => {
    setForm(saved);
    // Clean up any object URLs
    if (form.logo instanceof File) {
      URL.revokeObjectURL(URL.createObjectURL(form.logo));
    }
  };

  const handleInputChange = (field: keyof Omit<SettingsFormData, 'logo'>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageUpload = (file: File) => {
    // Clean up previous object URL if exists
    if (form.logo instanceof File) {
      URL.revokeObjectURL(URL.createObjectURL(form.logo));
    }
    
    // Store the file directly in form state
    setForm(prev => ({ ...prev, logo: file }));
    setUploadLoading(false);
    toast.success("Logo selected. Click 'Save Changes' to upload.");
  };


        const handleImageDelete = async () => {
        const [response] = await deleteLogo();
    
        if (response?.status) {
          toast.success(response.message || "Trigger deleted!");
          
        } else {
          toast.error(response?.detail || "Failed to delete trigger");
        }
      };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading settings...</div>
      </div>
    );
  }

  // Get logo URL for display (handle both string URL and File object)
  const getLogoForDisplay = () => {
    if (!form.logo) return null;
    if (typeof form.logo === 'string') return form.logo;
    if (form.logo instanceof File) return URL.createObjectURL(form.logo);
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <div>
        <div className="bg-white dark:bg-gray-900 rounded-t-xl p-8 pb-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>
          <div className="flex items-center gap-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab("profile")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "profile"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              Profile Settings
            </button>
            <button
              onClick={() => setActiveTab("conversation")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "conversation"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              Conversation Settings
            </button>
            <button
              onClick={() => setActiveTab("testing")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "testing"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              Testing & Preview
            </button>
          </div>
        </div>

        {activeTab === "profile" ? (
          <div className="mx-8">
            <ProfilePicture
              logo={getLogoForDisplay()}
              name={form.businessName || "Organization"}
              subtitle={isDirty ? "This will be displayed on your profile" : undefined}
              onDelete={handleImageDelete}
              onUpload={handleImageUpload}
              uploadLoading={uploadLoading}
              readOnly={isConversationAgent()}
            />

            <SettingsSection title="Basic info" description="Basic details associated with your profile">
              <Input
                name="firstName"
                label="First Name"
                value={form.firstName}
                onChange={handleInputChange("firstName")}
                readOnly={isConversationAgent()}
                className={isConversationAgent() ? "bg-gray-50 dark:bg-gray-800 cursor-not-allowed" : undefined}
              />
              <Input
                name="lastName"
                label="Last Name"
                value={form.lastName}
                onChange={handleInputChange("lastName")}
                readOnly={isConversationAgent()}
                className={isConversationAgent() ? "bg-gray-50 dark:bg-gray-800 cursor-not-allowed" : undefined}
              />
            </SettingsSection>

            <SettingsSection
              title="Business info"
              description="Your business details used for account setup and communication"
            >
              <Input
                name="businessName"
                label="Business Name"
                value={form.businessName}
                onChange={() => {}}
                readOnly
                variant="secondary"
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"

              />
              <Input
                name="website"
                label="Website"
                value={form.website}
                onChange={() => {}}
                readOnly
                variant="secondary"
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"

              />
              <Input
                name="businessLocation"
                label="Business Location"
                value={form.businessLocation}
                onChange={() => {}}
                readOnly
                variant="secondary"
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"

              />
            </SettingsSection>

            <SettingsSection
              title="Contact info"
              description="Your contact details for communication and updates"
            >
              <Input
                name="email"
                label="Email Address"
                value={form.email}
                onChange={() => {}}
                readOnly
                variant="secondary"
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"

              />
              <Input
                name="phone"
                label="Phone Number"
                value={form.phone}
                onChange={() => {}}
                readOnly
                variant="secondary"
                className="bg-gray-50 dark:bg-gray-800 cursor-not-allowed"

              />
            </SettingsSection>

            <SettingsActions
              isDirty={isDirty}
              onSave={handleSave}
              onDiscard={handleDiscard}
              saveLoading={saveLoading}
            />
          </div>
        ) : activeTab === "conversation" ? (
          <div className="mx-8 py-8">
            <ConversationSettings currentUserName={`${form.firstName} ${form.lastName}`.trim()} />
          </div>
        ) : (
          <div className="mx-8 py-8">
            <TestingPreviewSettings />
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;